import React, { useState, useEffect, useMemo } from 'react'
import { FaArrowRight, FaArrowLeft, FaFileCsv } from 'react-icons/fa'
import { Alert, Box, Stack, Heading, Text, Button, SimpleTable, Select, SearchableSelect, Badge, SimplePopover, Icon } from '@/components/lib'
import useStore from '../../../store'
import api from '../../../api'
import utils from '@/utils/utils'
import { TEAM_MEMBER_EVENT_TYPES, PARTICIPANT_EVENT_TYPES } from '@/utils/logEventTypes'

function TrialEvents ({ trial }) {
  const [page, setPage] = useState(1)
  const [userType, setUserType] = useState('user')
  const [eventType, setEventType] = useState('')
  const [logs, setLogs] = useState([])
  const [exporting, setExporting] = useState(false)
  const { team } = useStore()

  // The two tabs are written by different parts of the API and share almost no
  // event types, so the filter offers only the ones the current tab can show.
  const eventTypeOptions = useMemo(() => {
    const types = userType === 'participant' ? PARTICIPANT_EVENT_TYPES : TEAM_MEMBER_EVENT_TYPES
    return [{ value: '', label: 'All event types' }, ...types.map(t => ({ value: t, label: t }))]
  }, [userType])

  useEffect(() => {
    api.trials.getLogs(trial._id, page, userType, eventType, (data) => setLogs(data.logs))
  }, [trial._id, page, userType, eventType])

  function changeUserType (value) {
    setUserType(value)
    // Switching tabs would otherwise leave a filter in place that the new tab
    // never records, showing an empty log with no obvious reason why.
    setEventType('')
    setPage(1)
  }

  function changeEventType (value) {
    setEventType(value)
    setPage(1)
  }

  function exportCSV () {
    setExporting(true)
    api.trials.getLogsCsv(trial._id, userType, eventType, ({ csv }) => {
      const safeName = (trial.name || 'trial').replace(/ /g, '_')
      const suffix = eventType ? `${userType}_${eventType}` : userType
      utils.downloadFile(`data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`, `${safeName}_events_${suffix}.csv`)
      utils.toast('success', 'CSV file generated successfully', 'It should now be in your downloads folder.')
      setExporting(false)
    }, err => {
      utils.toast('error', err.message || 'Unable to export logs')
      setExporting(false)
    })
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h3' size='md'>Event logs</Heading>
        <Text>For audit purposes, Trialflare logs key actions taken by collaborators and participants of your trial.</Text>
      </Box>

      <Alert>You can find the event logs relating to the {team?.name} team on the team's settings page.</Alert>

      <Stack direction='row' alignItems='center' gap={3}>
        <Select onChange={e => changeUserType(e.target.value)} value={userType} maxW='200px' size='sm'>
          <option value='user'>Team member events</option>
          <option value='participant'>Participant events</option>
        </Select>
        <SearchableSelect
          size='sm'
          maxW='260px'
          options={eventTypeOptions}
          value={eventType}
          onChange={v => changeEventType(v)}
          placeholder='All event types'
        />
        <Button
          size='sm'
          variant='outline'
          onClick={exportCSV}
          loading={exporting}
        >
          <Icon as={FaFileCsv} /> Export logs
        </Button>
      </Stack>

      <Box>
        <SimpleTable
          asCard
          tableProps={{ size: 'sm', variant: 'striped' }}
          headers={['Date', 'User', 'Participant', 'Event', 'Data']}
          rows={logs.map(log => [
            utils.formatDateTime(log.createdAt),
            // Who performed the action. Participant-initiated events have no
            // user, so fall back to the participant who took it. A root entry
            // is only ever returned to another root user, so the badge is a
            // reminder that this row isn't one the customer can see.
            <Stack direction='row' alignItems='center' gap={2} key={`${log._id}-user`}>
              <Text fontSize='sm'>{log.userEmail || (log.isSystem ? 'System' : log.participantId) || '—'}</Text>
              {log.isRoot && <Badge colorPalette='purple'>Root</Badge>}
            </Stack>,
            // Who the action was about. For a participant's own action that is
            // the same person; for a team member's it's the participant they
            // acted on.
            log.participantId || '—',
            log.type,
            <Stack direction='row' key={log._id}>
              {log.apiToken &&
                <SimplePopover
                  key={`${log._id}-auth`}
                  on='hover'
                  trigger={<Badge>Auth</Badge>}
                  header='Authentication'
                  body={
                    <Box fontSize='2xs'>
                      <pre>{JSON.stringify(log.apiToken, null, 2)}</pre>
                    </Box>
                  }
                />}
              <SimplePopover
                on='hover'
                trigger={<Badge>Client</Badge>}
                header='Client information'
                body={
                  <Box fontSize='2xs'>
                    <pre>{JSON.stringify(log.client, null, 2)}</pre>
                  </Box>
}
              />

              <SimplePopover
                on='hover'
                trigger={<Badge>Data</Badge>}
                header='Event data'
                body={
                  <Box fontSize='2xs'>
                    <pre>{JSON.stringify(log.event, null, 2)}</pre>
                  </Box>
}
              />
            </Stack>
          ])}
        />
      </Box>

      <Box display='flex' justifyContent='space-between'>
        <Box>{page > 1 && <Button onClick={e => setPage(page - 1)}><FaArrowLeft /> Previous page</Button>}</Box>
        <Box>{logs.length > 0 && <Button onClick={e => setPage(page + 1)}><FaArrowRight /> Next page</Button>}</Box>
      </Box>
    </Stack>
  )
}

export default TrialEvents
